/**
 * Управление уведомлениями: кнопка в шапке + небольшая выпадашка.
 *
 * ГЛАВНОЕ ПРАВИЛО (постановка, пункт 2): Notification.requestPermission()
 * вызывается ТОЛЬКО из обработчика клика — никогда не из эффекта и не при
 * загрузке страницы. Браузеры (Chrome с 2019-го) молча отклоняют запросы
 * без жеста пользователя, а человек, которому вопрос задали «из ниоткуда»,
 * жмёт «Блокировать» — и это навсегда.
 *
 * Состояние permission показываем ЧЕСТНО: denied снять из интерфейса нельзя
 * (это решение браузера, а не сайта) — поэтому не рисуем бесполезную
 * активную кнопку, а объясняем, где это меняется.
 */
import { useEffect, useRef, useState } from "react";
import type { Messages } from "../i18n/messages";
type TKey = keyof Messages;
import { useT, useTF } from "../i18n";

import {
  loadNotificationSettings,
  saveNotificationSettings,
  SOUND_OPTIONS,
  type SoundId,
} from "../notifications/settings";
import { previewSound } from "../notifications/sound";
import { onRingDiag, previewCallSound } from "../notifications/callRing";
import { deliverSystemNotification } from "../notifications/systemNotification";
import { color, font, radius, space } from "./theme";

export function NotificationControls({ inline = false }: { inline?: boolean } = {}) {
  const t = useT();
  const tfmt = useTF();
  const [open, setOpen] = useState(inline);
  const [settings, setSettings] = useState(loadNotificationSettings);
  // permission перечитываем при каждом открытии панели: человек мог поменять
  // его в настройках браузера, пока вкладка была открыта.
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    typeof Notification === "undefined" ? "unsupported" : Notification.permission,
  );
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Результат последней диагностики — текстом в панели, чтобы человек видел
  // ответ браузера, а не догадывался о нём.
  const [diag, setDiag] = useState<string>("");
  // Диагностика звука звонка (дефект 4, Антон 12:47): показываем последний
  // исход startRinging — «звук запущен» или «звук заблокирован браузером».
  // Звук не зависит от Notification API: на iOS (unsupported) звук обязан
  // работать, и человек должен видеть, почему он молчит, если молчит.
  const [ringDiag, setRingDiag] = useState<string>("");

  useEffect(() => onRingDiag(setRingDiag), []);

  // Закрытие по клику вне панели — стандартное поведение выпадашек.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const toggleOpen = () => {
    if (!open && typeof Notification !== "undefined") {
      setPermission(Notification.permission);
    }
    setOpen((v) => !v);
  };

  const update = (next: typeof settings) => {
    setSettings(next);
    saveNotificationSettings(next);
  };

  /**
   * ЕДИНСТВЕННОЕ место, где мы просим разрешение. Вызывается из onClick —
   * жест пользователя есть, браузер покажет нативный диалог.
   */
  /**
   * ЕДИНСТВЕННОЕ место, где мы просим разрешение.
   *
   * SAFARI (проверка Антона 14:43: «Safari не запрашивал разрешение»):
   * у Safari на macOS Notification.requestPermission() исторически
   * КОЛЛБЭЧНЫЙ и промис не возвращает. Прежний код делал
   * `await Notification.requestPermission()` — в Safari это разрешалось в
   * undefined, нативный диалог не показывался, а состояние молча
   * оставалось "default". Поэтому поддерживаем ОБА вида вызова: если
   * результат не промис — ждём коллбэк.
   *
   * Второе требование Safari: вызов должен происходить СИНХРОННО внутри
   * обработчика жеста. Поэтому функция больше не async и не имеет ни одного
   * await до самого вызова — любое ожидание перед ним стоило бы нам жеста.
   */
  const enableNotifications = () => {
    if (typeof Notification === "undefined") return;
    const apply = (result: NotificationPermission) => {
      setPermission(result);
      if (result === "granted") update({ ...settings, enabled: true });
    };
    try {
      // Тип в TS описан как промис, но в Safari реально может прийти
      // undefined — сверяем в рантайме, а не доверяем декларации.
      const maybe = Notification.requestPermission(apply) as
        | Promise<NotificationPermission>
        | undefined;
      if (maybe && typeof maybe.then === "function") {
        void maybe.then(apply).catch(() => setPermission(Notification.permission));
      }
      // Если промиса нет — сработает коллбэк, переданный аргументом выше.
    } catch {
      setPermission(Notification.permission);
    }
  };

  /**
   * Пробное уведомление: показывает ровно то, что покажет входящее
   * сообщение. Нужно, чтобы отделить «браузер не дал разрешение» от
   * «разрешение есть, но наш код не дошёл до показа».
   */
  const testNotification = () => {
    setDiag("");
    void deliverSystemNotification("alg(success)", {
      body: t("notification.testBody"),
      tag: `alg-test-${Date.now()}`,
      requireInteraction: true,
      renotify: true,
      silent: false,
    })
      .then((result) => setDiag(result.verified ? t("notification.createdVerified") : t("notification.acceptedUnverified")))
      .catch((error) => setDiag(tfmt("notification.showError", { error: String(error) })));
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      {!inline && <button
        type="button"
        onClick={toggleOpen}
        title={t("profile.notifications")}
        aria-expanded={open}
        style={{
          background: "transparent",
          border: `1px solid ${color.border}`,
          borderRadius: radius.md,
          padding: "4px 10px",
          cursor: "pointer",
          fontSize: font.small,
          color: settings.enabled ? color.accent : color.textDim,
        }}
      >
        🔔
      </button>}
      {(inline || open) && (
        <div
          role="dialog"
          aria-label={t("notification.settingsTitle")}
          style={{
            position: inline ? "relative" : "absolute",
            right: inline ? undefined : 0,
            top: inline ? undefined : "calc(100% + 6px)",
            width: inline ? "100%" : 300,
            boxSizing: "border-box",
            background: color.bg,
            border: `1px solid ${color.border}`,
            borderRadius: radius.md,
            boxShadow: inline ? "none" : "0 6px 24px rgba(0,0,0,0.12)",
            padding: space.lg,
            zIndex: 30,
          }}
        >
          <div style={{ fontWeight: font.weightBold, marginBottom: space.md, fontSize: font.body }}>{t("profile.notifications")}</div>

          <div style={{ fontSize: font.small, color: color.textDim, marginBottom: space.md }}>
            {settings.enabled ? t("notification.appEnabled") : t("notification.appDisabled")}
          </div>

          <button
            type="button"
            onClick={testNotification}
            style={{
              width: "100%",
              marginBottom: space.md,
              padding: "6px 10px",
              borderRadius: radius.sm,
              border: `1px solid ${color.border}`,
              background: color.bg,
              color: color.text,
              cursor: "pointer",
              fontSize: font.small,
            }}
            data-testid="notif-test"
          >{t("notification.test")}</button>

          {diag && (
            <div data-testid="notif-diag" style={{ fontSize: font.small, color: color.textDim, marginBottom: space.md }}>
              {diag}
            </div>
          )}

          {permission === "default" && (
            <button
              type="button"
              onClick={enableNotifications}
              style={{
                width: "100%",
                marginBottom: space.md,
                padding: "8px 10px",
                background: color.accent,
                color: color.textOnAccent,
                border: "none",
                borderRadius: radius.md,
                cursor: "pointer",
                fontSize: font.body,
              }}
            >{t("notification.allow")}</button>
          )}

          <CategoryToggle
            checked={settings.enabled}
            label={t("notification.showNew")}
            onChange={(enabled) => update({ ...settings, enabled })}
          />

          <div style={{ marginTop: space.lg, marginBottom: space.sm, fontWeight: font.weightBold, fontSize: font.small }}>
            {t("notification.categoriesTitle")}
          </div>
          {([
            ["directMessages", "notification.categoryDirect"],
            ["groupMessages", "notification.categoryGroup"],
            ["mentions", "notification.categoryMentions"],
            ["replies", "notification.categoryReplies"],
            ["reactions", "notification.categoryReactions"],
            ["invites", "notification.categoryInvites"],
            ["incomingCalls", "notification.categoryCalls"],
          ] as const).map(([category, label]) => (
            <CategoryToggle
              key={category}
              checked={settings.categories[category]}
              label={t(label)}
              onChange={(value) => update({
                ...settings,
                categories: { ...settings.categories, [category]: value },
              })}
            />
          ))}

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: space.md,
              fontSize: font.body,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={settings.sound}
              onChange={(e) => update({ ...settings, sound: e.target.checked })}
            />{t("notification.soundNew")}</label>

          {/* ВЫКЛЮЧАТЕЛЬ ЗВОНКА (шаг 3, постановка: «звук должен отключаться —
              обязательно выключатель, иначе это станет пыткой»). Отдельный от
              звука сообщений: рингтон навязчивее, и гасить его не должно
              означать потерю тихого «бипа» о сообщениях. Показываем всегда,
              а не только при granted — звук звонка разрешения браузера не
              требует, требует лишь разблокированный жестом AudioContext. */}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: space.md,
              fontSize: font.body,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={settings.callSound}
              onChange={(e) => update({ ...settings, callSound: e.target.checked })}
            />{t("notification.ringOnCall")}</label>

          <SoundSelect
            label={t("notification.messageSoundLabel")}
            value={settings.messageSound}
            onChange={(messageSound) => update({ ...settings, messageSound })}
            onPreview={previewSound}
            t={t}
            testId="message-sound"
          />

          <SoundSelect
            label={t("notification.callSoundLabel")}
            value={settings.callTone}
            onChange={(callTone) => update({ ...settings, callTone })}
            onPreview={previewCallSound}
            t={t}
            testId="call-sound"
          />

          {/* ДИАГНОСТИКА ЗВУКА ЗВОНКА (дефект 4, Антон 12:47: «звука нет на
              всех девайсах»). Показываем последний исход startRinging:
              «звук запущен» или «звук заблокирован браузером». Это НЕ баннер
              звонка — панель диагностики, которую человек открывает сам. */}
          {ringDiag && (
            <div
              data-testid="ring-diag"
              style={{
                fontSize: font.small,
                fontFamily: "ui-monospace, monospace",
                color: color.textDim,
                marginTop: space.sm,
              }}
            >
              {ringDiag}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CategoryToggle({ checked, label, onChange }: {
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: space.md, fontSize: font.body, marginBottom: space.sm, cursor: "pointer" }}>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      {label}
    </label>
  );
}

function soundLabel(id: SoundId, t: (key: TKey) => string): string {
  if (id === "soft") return t("notification.soundSoft");
  if (id === "double") return t("notification.soundDouble");
  return t("notification.soundChime");
}

function SoundSelect({ label, value, onChange, onPreview, t, testId }: {
  label: string;
  value: SoundId;
  onChange: (value: SoundId) => void;
  onPreview: (value: SoundId) => void;
  t: (key: TKey) => string;
  testId: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: space.sm, marginTop: space.md }}>
      <label style={{ flex: "1 1 auto", fontSize: font.small, color: color.textDim }}>
        {label}
        <select aria-label={label} data-testid={testId} value={value}
          onChange={(event) => onChange(event.target.value as SoundId)}
          style={{ display: "block", width: "100%", marginTop: 4, padding: "6px 8px", border: `1px solid ${color.border}`, borderRadius: radius.sm, background: color.bg, color: color.text }}>
          {SOUND_OPTIONS.map((option) => <option key={option.id} value={option.id}>{soundLabel(option.id, t)}</option>)}
        </select>
      </label>
      <button type="button" onClick={() => onPreview(value)}
        aria-label={`${t("notification.previewSound")}: ${label}`}
        style={{ padding: "6px 10px", border: `1px solid ${color.border}`, borderRadius: radius.sm, background: color.bg, color: color.text, cursor: "pointer", fontSize: font.small }}>
        {t("notification.previewSound")}
      </button>
    </div>
  );
}
